// Real-time Updates Hook - Centralized WebSocket management
import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const WS_RECONNECT_DELAY = 3000;
const WS_MAX_RETRIES = 5;

export const useRealtime = () => {
    const { user, token } = useAuth();
    const wsRef = useRef(null);
    const [connected, setConnected] = useState(false);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [balance, setBalance] = useState({ usdt: 0, btc: 0, eth: 0 });
    const [prices, setPrices] = useState({});
    const [pnl, setPnl] = useState({ total_pnl: 0, today_pnl: 0, win_rate: 0 });
    const [recentTrades, setRecentTrades] = useState([]);
    const retriesRef = useRef(0);
    const listenersRef = useRef({});

    // Connect to WebSocket
    const connect = useCallback(() => {
        if (!token || !user) return;
        if (wsRef.current?.readyState === WebSocket.OPEN) return;

        const wsUrl = process.env.REACT_APP_BACKEND_URL
            .replace('https://', 'wss://')
            .replace('http://', 'ws://');
        
        try {
            // Use /api prefix for proper Kubernetes routing
            wsRef.current = new WebSocket(`${wsUrl}/api/ws/notifications/${token}`);

            wsRef.current.onopen = () => {
                setConnected(true);
                retriesRef.current = 0;
                console.log('WebSocket connected');
            };

            wsRef.current.onclose = () => {
                setConnected(false);
                // Auto-reconnect with backoff
                if (retriesRef.current < WS_MAX_RETRIES) {
                    retriesRef.current++;
                    setTimeout(connect, WS_RECONNECT_DELAY * retriesRef.current);
                }
            };

            wsRef.current.onerror = (error) => {
                console.error('WebSocket error:', error);
            };

            wsRef.current.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    handleMessage(message);
                } catch (e) {
                    console.error('Failed to parse WebSocket message:', e);
                }
            };
        } catch (error) {
            console.error('WebSocket connection error:', error);
        }
    }, [token, user]);

    // Handle incoming messages
    const handleMessage = useCallback((message) => {
        setLastUpdate(new Date());
        
        switch (message.type) {
            case 'initial_state':
                if (message.data.balance) setBalance(message.data.balance);
                if (message.data.prices) setPrices(message.data.prices);
                break;

            case 'balance_update':
                setBalance(prev => ({
                    ...prev,
                    usdt: message.data.usdt ?? prev.usdt,
                    btc: message.data.btc ?? prev.btc,
                    eth: message.data.eth ?? prev.eth
                }));
                // Notify listeners
                notifyListeners('balance', message.data);
                break;

            case 'trade_update':
                setRecentTrades(prev => [message.data, ...prev.slice(0, 19)]);
                // Show toast notification for bot trades
                if (message.is_bot_trade) {
                    const pnl = message.data.profit_loss;
                    const symbol = message.data.symbol;
                    if (pnl >= 0) {
                        toast.success(`🤖 Bot: ${symbol} +$${pnl.toFixed(2)}`);
                    } else {
                        toast.info(`🤖 Bot: ${symbol} -$${Math.abs(pnl).toFixed(2)}`);
                    }
                }
                notifyListeners('trade', message.data);
                break;

            case 'price_update':
                setPrices(message.data);
                notifyListeners('prices', message.data);
                break;

            case 'pnl_update':
                setPnl(message.data);
                notifyListeners('pnl', message.data);
                break;

            case 'deposit_update':
                toast.success(`Deposit ${message.data.status}: $${message.data.amount}`);
                notifyListeners('deposit', message.data);
                break;

            case 'withdrawal_update':
                toast.info(`Withdrawal ${message.data.status}: $${message.data.amount}`);
                notifyListeners('withdrawal', message.data);
                break;

            case 'full_sync':
                if (message.balance) {
                    setBalance(prev => ({
                        ...prev,
                        usdt: message.balance.current_balance ?? prev.usdt
                    }));
                }
                if (message.pnl) setPnl(message.pnl);
                break;

            case 'pong':
                // Heartbeat response
                break;

            default:
                console.log('Unknown message type:', message.type);
        }
    }, []);

    // Notify listeners
    const notifyListeners = (type, data) => {
        if (listenersRef.current[type]) {
            listenersRef.current[type].forEach(callback => callback(data));
        }
    };

    // Subscribe to specific events
    const subscribe = useCallback((type, callback) => {
        if (!listenersRef.current[type]) {
            listenersRef.current[type] = [];
        }
        listenersRef.current[type].push(callback);

        // Return unsubscribe function
        return () => {
            listenersRef.current[type] = listenersRef.current[type].filter(cb => cb !== callback);
        };
    }, []);

    // Request full sync
    const requestSync = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('sync');
        }
    }, []);

    // Send ping
    const ping = useCallback(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send('ping');
        }
    }, []);

    // Connect on mount
    useEffect(() => {
        connect();
        
        // Heartbeat interval
        const pingInterval = setInterval(ping, 30000);
        
        return () => {
            clearInterval(pingInterval);
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, [connect, ping]);

    // Reconnect when token changes
    useEffect(() => {
        if (token) {
            connect();
        }
    }, [token, connect]);

    return {
        connected,
        lastUpdate,
        balance,
        prices,
        pnl,
        recentTrades,
        subscribe,
        requestSync,
        ping
    };
};

// Admin-specific real-time hook
export const useAdminRealtime = () => {
    const { token, user } = useAuth();
    const wsRef = useRef(null);
    const [connected, setConnected] = useState(false);
    const [stats, setStats] = useState(null);
    const [botTrades, setBotTrades] = useState([]);
    const [newDeposits, setNewDeposits] = useState([]);

    const connect = useCallback(() => {
        if (!token || !user?.is_admin) return;

        const wsUrl = process.env.REACT_APP_BACKEND_URL
            .replace('https://', 'wss://')
            .replace('http://', 'ws://');
        
        try {
            wsRef.current = new WebSocket(`${wsUrl}/ws/admin/${token}`);

            wsRef.current.onopen = () => {
                setConnected(true);
                // Request initial stats
                wsRef.current.send('stats');
            };

            wsRef.current.onclose = () => {
                setConnected(false);
                // Auto-reconnect
                setTimeout(connect, 5000);
            };

            wsRef.current.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    switch (message.type) {
                        case 'stats':
                            setStats(message.data);
                            break;
                        case 'bot_trade':
                            setBotTrades(prev => [message, ...prev.slice(0, 49)]);
                            break;
                        case 'new_deposit':
                            setNewDeposits(prev => [message, ...prev.slice(0, 19)]);
                            toast.success(`New deposit from ${message.user_id?.slice(0, 8)}: $${message.data?.amount}`);
                            break;
                        case 'new_withdrawal':
                            toast.info(`New withdrawal request`);
                            break;
                    }
                } catch (e) {
                    console.error('Admin WS parse error:', e);
                }
            };
        } catch (error) {
            console.error('Admin WebSocket error:', error);
        }
    }, [token, user]);

    useEffect(() => {
        connect();
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, [connect]);

    return {
        connected,
        stats,
        botTrades,
        newDeposits
    };
};

export default useRealtime;
